/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
    AnalogueFilterOptionsState, 
    IAnalogueFilterOptions, 
    IAnalogueFilters,
    IAnalogueTagAttributeAllowedValue
} from './types';
import { 
    GetAnalogueTagAttributeAllowedValuesThunk, 
    GetAnalogueDropdownAllTagAttributeAllowedValuesThunk 
} from './thunk';

const initialFilters: IAnalogueFilters = {
    id: '',
    title: '',
    type: 1,
    xTagAttributeId: '',
    yTagAttributeId: '',
    quickPlotId: '',
    plotOptions: {
        axisOptions: {
            xMin: null,
            xMax: null,
            yMin: null,
            yMax: null,
            xGridlines: true,
            yGridlines: true,
            xLogarithmic: false,
            yLogarithmic: false,
            xInvert: false,
            yInvert: false
        },
        regressions: {
            addPoint: false,
            expRegression: false,
            label: '',
            linRegression: false,
            logRegression: false,
            powRegression: false,
            xLogLines: false,
            xValue: null,
            yLogLines: false,
            zeroIntercept: false,
            zeroInterceptValue: 0,
        },
        legends: {
            showFilters: true,
            showUserData: true,
            showRegressionEquations: true
        },
        labels: {
            showUserDataLabels: false,
            globalDatabaseLabels: false,
            showAxisLabels: true
        },
        tooltips: []
    },
    categorizeOptions: {
        tagAttributeId: null,
        enabled: false,
        logScale: false,
        applyToUserData: false
    },
    filterOptions: {
        applyToUserData: false,
        applyToBenchmarkData: false,
        isEnabled: true,
        applyToBenchmarkPlots: false,
        filters: []
    },
    labelTemplateHover: '</br><b>#value</b></br>',
    categoryTemplateHover: '<b>#key:</b> #value</br>',
    tooltipTemplateHover: '<b>#key:</b> #value</br>',
    extraTemplateHover: '<extra></extra>',
};

const initialState: AnalogueFilterOptionsState = {
    filters: initialFilters,
    tagAttributeAllowedValues: [],
    dropdownAllTagAttributeValues: [],
    loadAllowedValues: false,
    isLoading: false,
    error: null,
};

export const analogueFilterOptionsSlice = createSlice({
    name: 'analogueFilterOptions',
    initialState,
    reducers: {
        setAnalogueFilterOptionItem: (state, action) => {
            const { element, value } = action.payload;
            if (element === "isEnabled") {
                // Handle filter enabled/disabled state if needed
            }
            state.filters.filterOptions[element as keyof IAnalogueFilterOptions] = value;
        },
        
        setAnalogueTagAttributeAllowedValues: (state, action) => {
            state.tagAttributeAllowedValues = action.payload;
        },
        
        setAnalogueDropdownAllTagAttributeValues: (state, action) => {
            state.dropdownAllTagAttributeValues = action.payload;
        },
        
        setAnalogueLoadAllowedValues: (state, action) => {
            state.loadAllowedValues = action.payload;
        },
        
        applyAnalogueFilter: (state, action) => {
            const { props } = action.payload;
            const { item } = props;
            
            // Update filter applied state for the specific item
            // This would need to be implemented based on your tree structure
            // For now, just update the filter in the filterOptions
            const existingFilterIndex = state.filters.filterOptions.filters.findIndex(
                f => f.tagAttributeId === item.item.id
            );
            
            if (existingFilterIndex >= 0 && item.item.values?.length === 0 && !item.item.firstConditionInput && !item.item.secondConditionInput) {
                // Remove filter if no values or conditions
                state.filters.filterOptions.filters.splice(existingFilterIndex, 1);
            } else if (existingFilterIndex >= 0) {
                // Update existing filter
                state.filters.filterOptions.filters[existingFilterIndex] = {
                    tagAttributeId: item.item.id,
                    values: item.item.values || [],
                    partOne: item.item.firstConditionInput ? {
                        value: item.item.firstConditionInput,
                        ignoreCase: true,
                        operationId: item.item.firstConditionList?.id || null,
                        operationNameNumeric: item.item.firstConditionList?.name || null,
                        operationNameString: item.item.firstConditionList?.name || null
                    } : null,
                    booleanOperationId: item.item.logicalOperationList?.id || null,
                    partTwo: item.item.secondConditionInput ? {
                        value: item.item.secondConditionInput,
                        ignoreCase: true,
                        operationId: item.item.secondConditionList?.id || null,
                        operationNameNumeric: item.item.secondConditionList?.name || null,
                        operationNameString: item.item.secondConditionList?.name || null
                    } : null
                };
            } else if (item.item.values?.length > 0 || item.item.firstConditionInput || item.item.secondConditionInput) {
                // Add new filter
                state.filters.filterOptions.filters.push({
                    tagAttributeId: item.item.id,
                    values: item.item.values || [],
                    partOne: item.item.firstConditionInput ? {
                        value: item.item.firstConditionInput,
                        ignoreCase: true,
                        operationId: item.item.firstConditionList?.id || null,
                        operationNameNumeric: item.item.firstConditionList?.name || null,
                        operationNameString: item.item.firstConditionList?.name || null
                    } : null,
                    booleanOperationId: item.item.logicalOperationList?.id || null,
                    partTwo: item.item.secondConditionInput ? {
                        value: item.item.secondConditionInput,
                        ignoreCase: true,
                        operationId: item.item.secondConditionList?.id || null,
                        operationNameNumeric: item.item.secondConditionList?.name || null,
                        operationNameString: item.item.secondConditionList?.name || null
                    } : null
                });
            }
        },
        
        clearAnalogueFilter: (state, action) => {
            const { item, tagAttribute } = action.payload;
            
            // Remove the filter from filterOptions
            state.filters.filterOptions.filters = state.filters.filterOptions.filters.filter(
                filter => filter.tagAttributeId !== item.item.id
            );
            
            // Reset any tag attribute values if they exist
            if (Array.isArray(state.tagAttributeAllowedValues)) {
                state.tagAttributeAllowedValues = state.tagAttributeAllowedValues.map(tag => {
                // Create a new tag object with values that have checked=false
                return {
                    ...tag,
                    values: tag.values.map(value => ({
                        ...value,
                        checked: false
                    }))
                };
            });
            }
        },
        
        resetAnalogueFilters: (state) => {
            state.filters.filterOptions.filters = [];
            state.tagAttributeAllowedValues = [];
            state.dropdownAllTagAttributeValues = [];
        },
    },
    
    extraReducers: (builder) => {
        builder
            // GetAnalogueTagAttributeAllowedValuesThunk
            .addCase(GetAnalogueTagAttributeAllowedValuesThunk.pending, (state) => {
                state.loadAllowedValues = true;
                state.isLoading = true;
            })
            .addCase(GetAnalogueTagAttributeAllowedValuesThunk.fulfilled, (state, action) => {
                state.tagAttributeAllowedValues = action.payload;
                state.loadAllowedValues = false;
                state.isLoading = false;
                state.error = null;
            })
            .addCase(GetAnalogueTagAttributeAllowedValuesThunk.rejected, (state, action) => {
                state.loadAllowedValues = false;
                state.isLoading = false;
                state.error = action.error.message || 'Failed to load allowed values';
            })
            
            // GetAnalogueDropdownAllTagAttributeAllowedValuesThunk
            .addCase(GetAnalogueDropdownAllTagAttributeAllowedValuesThunk.pending, (state) => {
                state.loadAllowedValues = true;
                state.isLoading = true;
            })
            .addCase(GetAnalogueDropdownAllTagAttributeAllowedValuesThunk.fulfilled, (state, action) => {
                state.dropdownAllTagAttributeValues = action.payload;
                state.loadAllowedValues = false;
                state.isLoading = false;
                state.error = null;
            })
            .addCase(GetAnalogueDropdownAllTagAttributeAllowedValuesThunk.rejected, (state, action) => {
                state.loadAllowedValues = false;
                state.isLoading = false;
                state.error = action.error.message || 'Failed to load dropdown allowed values';
            });
    },
});

export const {
    setAnalogueFilterOptionItem,
    setAnalogueTagAttributeAllowedValues,
    setAnalogueDropdownAllTagAttributeValues,
    setAnalogueLoadAllowedValues,
    applyAnalogueFilter,
    clearAnalogueFilter,
    resetAnalogueFilters,
} = analogueFilterOptionsSlice.actions;

export default analogueFilterOptionsSlice.reducer; 